import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { sanitizeDocumentHtml } from '../utils/sanitizeHtml';

// 実際のデザイン仕様
const DESIGN_SPEC = {
  "design_theme": {
    "name": "Ramen Evolution 2025",
    "colors": {
      "text": "#212121",
      "error": "#F44336",
      "accent": "#1E88E5",
      "primary": "#C62828",
      "success": "#4CAF50",
      "warning": "#FF9800",
      "secondary": "#FBC02D",
      "background": "#FDFDFD"
    },
    "typography": {
      "body_font": {
        "size": "22px",
        "family": "Noto Sans JP",
        "weight": "normal"
      },
      "title_font": {
        "size": "44px",
        "family": "Noto Sans JP",
        "weight": "bold"
      },
      "caption_font": {
        "size": "14px",
        "family": "Noto Sans JP",
        "weight": "light"
      }
    },
    "description": "ラーメンの伝統的な温かみと未来への革新性を融合させた、モダンでプロフェッショナルなデザインテーマ。"
  },
  "slide_designs": [
    {
      "layout": "centered_with_background_image",
      "elements": [
        {
          "type": "title",
          "style": {
            "size": { "width": "auto", "height": "auto" },
            "color": "#FFFFFF",
            "fontSize": "56px",
            "position": { "x": "center", "y": "45%" },
            "animation": "fade-in-up",
            "textAlign": "center"
          },
          "content": "2025年に向けた新しいラーメン：進化する食文化とトレンド"
        },
        {
          "type": "subtitle",
          "style": {
            "size": { "width": "auto", "height": "auto" },
            "color": "#FBC02D",
            "delay": "0.5s",
            "fontSize": "24px",
            "position": { "x": "center", "y": "58%" },
            "animation": "fade-in-up",
            "textAlign": "center"
          },
          "content": "ラーメンの最新トレンドと未来像を探る"
        }
      ],
      "slide_id": 1,
      "background": {
        "type": "gradient",
        "value": "linear-gradient(135deg, #C62828 0%, #FBC02D 100%)",
        "overlay": "rgba(33, 33, 33, 0.6)"
      },
      "slide_type": "title_slide",
      "chapter_ref": 0
    },
    {
      "layout": "two_column",
      "elements": [
        {
          "type": "title",
          "style": {
            "size": { "width": "auto", "height": "auto" },
            "color": "#C62828",
            "position": { "x": "10%", "y": "15%" }
          },
          "content": "Agenda"
        },
        {
          "type": "list",
          "style": {
            "size": { "width": "auto", "height": "auto" },
            "fontSize": "28px",
            "position": { "x": "10%", "y": "30%" },
            "lineHeight": "1.8"
          },
          "content": [
            "1. ラーメンの進化：過去から現在まで",
            "2. 2025年のラーメン：未来予測",
            "3. 新たなラーメン体験：テクノロジーの活用",
            "4. まとめ：ラーメンの未来"
          ]
        }
      ],
      "slide_id": 2,
      "background": {
        "type": "solid",
        "value": "#FFFFFF"
      },
      "slide_type": "agenda_slide",
      "chapter_ref": 0
    }
    // 他のスライドは省略（実際には全22スライドを含める）
  ]
};

const DocumentCreationTest: React.FC = () => {
  const [generatedHTML, setGeneratedHTML] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [iteration, setIteration] = useState(0);

  // /api/test/generate-html は未認証エンドポイントで、Gemini が組み立てた完全な
  // HTML ドキュメント（head の <style> に CSS）をそのまま返す。描画前にサニタイズ
  // する。CSS を残す必要があるので WHOLE_DOCUMENT 版を使う。
  const sanitizedHTML = useMemo(
    () => sanitizeDocumentHtml(generatedHTML),
    [generatedHTML],
  );

  const generateHTML = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // APIエンドポイントを直接呼び出す（実際のステージ3のエンドポイント）
      const response = await axios.post('/api/test/generate-html', {
        designSpec: DESIGN_SPEC,
        contentData: {
          content: {
            title: "2025年に向けた新しいラーメン：進化する食文化とトレンド"
          },
          analysis: {
            theme: "2025年に向けた新しいラーメン：進化する食文化とトレンド",
            purpose: "ラーメン業界の未来予測と革新的なトレンドの紹介",
            key_messages: [
              "伝統と革新の融合",
              "テクノロジーによる新体験",
              "持続可能な食文化"
            ]
          }
        }
      });
      
      if (response.data.html) {
        setGeneratedHTML(response.data.html);
        setIteration(prev => prev + 1);
      } else {
        setError('HTMLの生成に失敗しました');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'エラーが発生しました');
      console.error('HTML生成エラー:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="document-creation-test mx-auto max-w-[1400px] p-5">
      <div className="mb-[30px] text-center">
        <h1 className="mb-2.5 text-[28px] text-[#333]">デザイン改善テストページ</h1>
        <p className="text-[18px] text-[#666]">イテレーション: {iteration}/10</p>
      </div>

      <div className="mb-[30px] flex flex-col items-center gap-[15px]">
        <button
          onClick={generateHTML}
          disabled={isLoading}
          className="cursor-pointer rounded border-none bg-[#C62828] px-[30px] py-3 text-[16px] text-white transition-[background-color] duration-300 ease-[ease] hover:enabled:bg-[#8B0000] disabled:cursor-not-allowed disabled:bg-[#ccc]"
        >
          {isLoading ? '生成中...' : '第3段階: HTML生成'}
        </button>

        {error && (
          <div className="col-span-full mb-4 flex items-center justify-between rounded border border-[#fecaca] bg-[#ffebee] px-5 py-2.5 text-center text-[14px] text-[#c62828]">
            {error}
          </div>
        )}
      </div>

      <div className="stage-result-tabs relative flex h-full min-h-[500px] flex-col overflow-hidden rounded-lg border border-[#ddd] bg-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] max-[768px]:min-h-[400px]">
        <div className="min-h-[600px] overflow-auto rounded-lg bg-[#f5f5f5] p-5">
          {generatedHTML ? (
            <div dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />
          ) : (
            <div className="flex h-[500px] items-center justify-center text-[18px] text-[#999]">
              生成されたHTMLがここに表示されます
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentCreationTest;